import { Injectable, NgZone } from '@angular/core';

import { Observable, Subject, Subscription, debounceTime, fromEvent } from 'rxjs';

import {
    LineUtil, DomUtil, Point as LPoint,
    DomEvent, FeatureGroup, Map, LatLng, Circle, CircleMarkerOptions,
    Polygon, Polyline, PolylineOptions, LeafletMouseEvent, Marker, Icon,
    Popup, DivIcon, Layer, polyline, Path, latLng, point, latLngBounds
} from 'leaflet';

import { MapService } from '../../map/map.service';

import { AreaTypeEnum } from '../../../../enums/area-type.enum';
import { Point } from '../../../../models/domain/Point';
import { Area } from '../../../../models/domain/Area';
import { Consts } from '../../../../consts/consts';
import { TranslateService } from '@ngx-translate/core';

export enum State {
    Default,

    CircleOrigin,
    CircleRadiusInit,
    CircleRadius,
    CircleDone,

    PolygonFirstVertex,
    PolygonSecondVertexInit,
    PolygonSecondVertex,
    PolygonDone,

    LineFirstPoint,
    LineSecondPointInit,
    LineSecondPoint,
    LineDone
}


class CreatorMarker extends Circle {
    segment?: LatLng[];
}


/**
 * Provides area drawing utilities
 */
@Injectable()
export class AreaDrawerService {

    /**
     * Area to draw
     */
    private area?: Area;

    /**
     * Feature group, needed for hiding/showing area on map
     */
    private group?: FeatureGroup;

    /**
    * Subject for sending drawn area
    */
    private drawnAreaSubject: Subject<Area> = new Subject<Area>();

    /**
     * Returns drawn area stream
     */
    public get drawnArea$(): Observable<Area> {
        return this.drawnAreaSubject.asObservable();
    }

    /**
     * State
     */
    private state: State = State.Default;

    /**
     * Setter of service state
     */
    public set areaDrawerState(state: State) {
        this.state = state;
    }

    /**
     * Flag indicating whether given area should be in edition mode
     */
    private inEdition?: boolean;

    /**
     * Flag indicating whether shape is editable
     */
    private editable?: boolean;

    /**
     * Subject for selected arena communication: area drawer service -> list lite component
     */
    public selectedAreaSubject: Subject<Area | undefined> = new Subject<Area | undefined>();

    /**
     * Subject for drawing ended signal communication: area create component & area form component -> area drawer service
     */
    public drawingDoneSubject: Subject<void> = new Subject<void>();

    /**
     * Point creator's marker's movement event handler function: needed for removing after drawing is done
     */
    private creatorMovementHandler: (event: LeafletMouseEvent) => void;

    /**
     * Point creator's marker's click event handler function: needed for removing after drawing is done
     */
    private creatorClickHandler: (event: LeafletMouseEvent) => void;

    /**
     * Point creator marker
     */
    private creator?: CreatorMarker;

    /**
     * Latlng of previous mouse down on shape - needed for smooth translation of shape
     */
    private mousePrev?: LatLng;

    /**
     * Flag indicating whether circle slider is in fixed position
     */
    private IS_SLIDER_FIXED = true;

    /**
     * Default area thickness
     */
    private CIRCLE_DEFAULT_THICKNESS = 1;
    private LINE_DEFAULT_THICKNESS = 1;
    private POLYGON_DEFAULT_THICKNESS = 1;

    private AREA_COLOR = '#4196FF';
    private AREA_COLOR_OPACITY = 0.15;

    /**
     * Costructor of service
     * @param $map map service
     */


    public onClickEditTooltipArea$: Subject<Area> = new Subject();
    public onClickDeleteTooltipArea$: Subject<Area> = new Subject();

    public constructor(
        private $map: MapService,
        private $zone: NgZone,
        private translate: TranslateService,
    ) {

        // Build creator movement event handler callback
        this.creatorMovementHandler = (event) => {
            const mouse = (event as LeafletMouseEvent).latlng;
            if (this.creator && this.group) {
                this.group.removeLayer(this.creator);
                this.creator = undefined;
            }
            this.creator = this.getCreatorMarker(this.area!, mouse);
            if (this.creator && this.group) {
                this.group.addLayer(this.creator);
            }
        };

        // Build creator click event handler callback
        this.creatorClickHandler = (event) => {

            this.$zone.run(() => {

                if (this.creator) {
                    // Add new vertex to area
                    this.addVertexToArea(this.creator.getLatLng(), this.area!);
                    // Push changes
                    this.drawnAreaSubject.next(this.area!);
                    // Redraw area
                    this.remove();
                    this.setArea(this.area, this.inEdition);
                    this.draw();
                }
            });
        };

        // When drawing is done
        this.drawingDoneSubject.subscribe(nothing => {
            this.removeCreatorEventHandlers();
        });

        // Subscribe for map events
        this.$map.mapEvent$.subscribe((mapEvent: any) => {

            // Exit if area does not exist or existing area is being modificated
            if (!this.area || this.area.Id) {
                return;
            }

            // Exit when shape is done
            if (this.state === State.CircleDone ||
                this.state === State.LineDone ||
                this.state === State.PolygonDone) {
                return;
            }

            switch (this.area.Tp) {

                // Circle
                case AreaTypeEnum.Circle:

                    if (this.state === State.Default) {
                        this.state = State.CircleOrigin;
                    }

                    // Origin
                    if (this.state === State.CircleOrigin) {
                        // Mouse down
                        if (mapEvent.mapEventType === 'mousedown') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const origin: Point = {
                                Lat: event.latlng.lat.toString(),
                                Lon: event.latlng.lng.toString(),
                            };
                            this.area.Coords.push(origin);
                            this.state = State.CircleRadiusInit;
                        }
                    }

                    // Radius init
                    if (this.state === State.CircleRadiusInit) {
                        // Mouse move
                        if (mapEvent.mapEventType === 'mousemove') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const pointOnCircle: Point = {
                                Lat: this.area.Coords[0].Lat,
                                Lon: event.latlng.lng.toString()
                            };
                            this.area.Coords.push(pointOnCircle);
                            this.state = State.CircleRadius;
                        }
                    }

                    // Radius
                    if (this.state === State.CircleRadius) {
                        // Mouse move
                        if (mapEvent.mapEventType === 'mousemove') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const pointOnCircle = this.area.Coords[1];
                            let pointLat_: string;
                            let pointLon_: string;
                            if (this.IS_SLIDER_FIXED) {
                                const zoom = this.$map.map!.getZoom();
                                const origin = this.convertToLatLng(this.area.Coords[0]);
                                const originPro = this.$map.map!.project(origin, zoom);
                                const eventPro = this.$map.map!.project(event.latlng, zoom);
                                const xDiff = eventPro.x - originPro.x;
                                const yDiff = eventPro.y - originPro.y;
                                const distance = Math.sqrt(Math.pow(xDiff, 2) + Math.pow(yDiff, 2));
                                const pointPro = originPro.clone();
                                pointPro.x += distance;
                                const _point = this.$map.map!.unproject(pointPro, zoom);
                                pointLat_ = _point.lat.toString();
                                pointLon_ = _point.lng.toString();
                            } else {
                                pointLat_ = event.latlng.lat.toString();
                                pointLon_ = event.latlng.lng.toString();
                            }
                            pointOnCircle.Lat = pointLat_;
                            pointOnCircle.Lon = pointLon_;



                            this.drawnAreaSubject.next(this.area);
                        }
                        // Mouse up
                        if (mapEvent.mapEventType === 'mouseup') {
                            this.state = State.CircleDone;
                        }
                    }

                    break;


                // Polygon
                case AreaTypeEnum.Polygon:

                    if (this.state === State.Default) {
                        this.state = State.PolygonFirstVertex;
                    }

                    // First vertex
                    if (this.state === State.PolygonFirstVertex) {
                        // Mouse down
                        if (mapEvent.mapEventType === 'mousedown') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const v1: Point = {
                                Lat: event.latlng.lat.toString(),
                                Lon: event.latlng.lng.toString(),
                            };
                            this.area.Coords.push(v1);
                            this.state = State.PolygonSecondVertexInit;
                        }
                    }

                    // Second vertex init
                    if (this.state === State.PolygonSecondVertexInit) {
                        // Mouse move
                        if (mapEvent.mapEventType === 'mousemove') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const v1 = this.area.Coords[0];
                            const v2: Point = { Lat: v1.Lat, Lon: event.latlng.lng.toString() };
                            const v3: Point = { Lat: event.latlng.lng.toString(), Lon: event.latlng.lng.toString() };
                            const v4: Point = { Lat: event.latlng.lng.toString(), Lon: v1.Lon };
                            this.area.Coords.push(v2);
                            this.area.Coords.push(v3);
                            this.area.Coords.push(v4);
                            this.state = State.PolygonSecondVertex;
                        }
                    }

                    // Second vertex
                    if (this.state === State.PolygonSecondVertex) {
                        // Mouse move
                        if (mapEvent.mapEventType === 'mousemove') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const v1 = this.area.Coords[0];
                            const v2 = this.area.Coords[1];
                            const v3 = this.area.Coords[2];
                            const v4 = this.area.Coords[3];
                            v2.Lon = event.latlng.lng.toString();
                            v3.Lat = event.latlng.lat.toString();
                            v3.Lon = event.latlng.lng.toString();
                            v4.Lat = event.latlng.lat.toString();
                            this.drawnAreaSubject.next(this.area);
                        }
                        // Mouse up
                        if (mapEvent.mapEventType === 'mouseup') {
                            this.state = State.PolygonDone;
                        }
                    }

                    break;


                // Line
                case AreaTypeEnum.Line:

                    if (this.state === State.Default) {
                        this.state = State.LineFirstPoint;
                    }

                    // First point
                    if (this.state === State.LineFirstPoint) {
                        // Mouse down
                        if (mapEvent.mapEventType === 'mousedown') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const first: Point = {
                                Lat: event.latlng.lat.toString(),
                                Lon: event.latlng.lng.toString(),
                            };
                            this.area.Coords.push(first);
                            this.state = State.LineSecondPointInit;
                        }
                    }

                    // Second point init
                    if (this.state === State.LineSecondPointInit) {
                        // Mouse move
                        if (mapEvent.mapEventType === 'mousemove') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const second: Point = {
                                Lat: event.latlng.lat.toString(),
                                Lon: event.latlng.lng.toString(),
                            };
                            this.area.Coords.push(second);
                            this.state = State.LineSecondPoint;
                        }
                    }

                    // Second point
                    if (this.state === State.LineSecondPoint) {
                        // Mouse move
                        if (mapEvent.mapEventType === 'mousemove') {
                            const event = mapEvent.event as LeafletMouseEvent;
                            const second = this.area.Coords[1];
                            second.Lat = event.latlng.lat.toString();
                            second.Lon = event.latlng.lng.toString();
                            this.drawnAreaSubject.next(this.area);
                        }
                        // Mouse up
                        if (mapEvent.mapEventType === 'mouseup') {
                            this.state = State.LineDone;
                        }
                    }

                    break;
            }

            // Redraw area
            this.remove();
            this.setArea(this.area, this.inEdition);
            this.draw();
        });
    }


    /**
     * Sets area to draw
     * @param area area to draw
     * @param inEdition flag indicating whether given area should be in edition mode
     * @param editable flag indicating whether given area is editable
     */
    public setArea(area?: Area, inEdition: boolean = false, editable: boolean = true): void {
        this.inEdition = inEdition;
        this.editable = editable;

        // When editing existing shape set state to <shape>Done
        if (area && area.Id) {
            if (area.Tp === AreaTypeEnum.Circle) {
                this.state = State.CircleDone;
            } else if (area.Tp === AreaTypeEnum.Polygon) {
                this.state = State.PolygonDone;
            } else if (area.Tp === AreaTypeEnum.Line) {
                this.state = State.LineDone;
            }
        }

        // Set up stuff according to area type
        if (area && this.inEdition) {
            if (area.Tp === AreaTypeEnum.Circle) {
                // empty
            } else if (area.Tp === AreaTypeEnum.Polygon) {
                this.addCreatorEventHandlers();
            } else if (area.Tp === AreaTypeEnum.Line) {
                this.addCreatorEventHandlers();
            }
        }

        this.area = area;
    }


    /**
     * Draws given area on a map
     */
    public draw(): any {

        if (!this.area) {
            return;
        }

        if (this.$map.map && this.inEdition) {
            this.$map.map.dragging.disable();
            DomUtil.addClass(this.$map.map.getContainer(), 'crosshair-cursor-enabled');
        }

        this.area = { 
            ...this.area,
            Cl: this.area?.Cl?.replace('00', ''),
        };
        if (this.area.Tp === AreaTypeEnum.Circle) {
            return this.drawCircle(this.area, this.inEdition!, this.editable!);

        } else if (this.area.Tp === AreaTypeEnum.Polygon) {
           return  this.drawPolygon(this.area, this.inEdition!, this.editable!);

        } else if (this.area.Tp === AreaTypeEnum.Line) {
            return this.drawLine(this.area, this.inEdition!, this.editable!);
        }
    }


    /**
     * Removes all drawn areas
     */
    public remove(): void {

        if (this.$map.map) {
            this.$map.map.dragging.enable();
            DomUtil.removeClass(this.$map.map.getContainer(), 'crosshair-cursor-enabled');
        }

        this.$map.groups.areas.clearLayers();
        this.group = undefined;

        this.removeCreatorEventHandlers();
    }


    /**
     * Fit to bounds to visible area
     */
    public fitBoundsToVisibleArea(bounds: any = this.$map.groups.areas.getBounds()): void {
        if (bounds && bounds.isValid()) {
            this.$map.map!.fitBounds(bounds, Consts.MapPadding);
        }
    }



    private calculateProperPoint(a: any, b: any, r: number): any {

        const sin = Math.sin;
        const asin = Math.asin;
        const cos = Math.cos;
        const atan2 = Math.atan2;

        const R = 6371e3;

        const aLat = parseFloat(a.Lat) * Math.PI / 180;
        const aLng = parseFloat(a.Lon) * Math.PI / 180;
        const bLat = aLat; // parseFloat(b.Lat) * Math.PI / 180;

        const diff = r * (360 / (cos(aLat) * R));

        // const bLng = parseFloat(b.Lon) * Math.PI / 180;
        const bLng = aLng + diff;



        // calculate bearing
        const X = sin(bLng - aLng) * cos(bLat);
        const Y = cos(aLat) * sin(bLat) - sin(aLat) * cos(bLat) * cos(bLng - aLng);
        const B = atan2(X, Y);

        const _cLat = asin(sin(aLat) * cos(r / R) + cos(aLat) * sin(r / R) * cos(B));
        const _c1 = (sin(B) * sin(r / R) * cos(aLat));
        const _c2 = (cos(r / R) - sin(aLat) * sin(_cLat));
        const _c3 = atan2(_c1, _c2);
        const _cLng = aLng + _c3;

        const cLat = _cLat * 180 / Math.PI;
        const cLng = _cLng * 180 / Math.PI;

        const _b = <Point>{ Lat: cLat.toString(), Lon: cLng.toString() };

        return _b;
    }

    /**
     * Draws circle on a map
     * @param area area to draw
     * @param inEdition flag indicating whether given area should be in edition mode
     * @param editable flag indicating whether given area is editable
     */
    private drawCircle(area: Area, inEdition: boolean, editable: boolean): Circle<any> {

        // Area Group
        this.group = new FeatureGroup();
        this.$map.groups.areas.addLayer(this.group);

        if (area.Coords.length === 1) {

            // Small radius
            const origin = this.convertToLatLng(area.Coords[0]);
            const radius = 30;  // in pixels

            // Options
            const weight = this.getWeightInPixels(area);
            const options: CircleMarkerOptions = { weight };

            // Draw circle
            const circle = new Circle(origin, radius, options);
            this.group.addLayer(circle);

        } else if (area.Coords.length === 2) {

            // Compute radius
            const origin = this.convertToLatLng(area.Coords[0]);
            const pointOnCircle = this.convertToLatLng(area.Coords[1]);
            const radius = this.$map.map!.distance(origin, pointOnCircle);  // in pixels



            // Options
            const weight = this.getWeightInPixels(area);
            const options: any = {
                weight,
                fillOpacity: this.AREA_COLOR_OPACITY,
                color: area?.Cl || this.AREA_COLOR,
                areaData: { ...area },
            };

            // Draw circle
            const circle = new Circle(origin, radius, options);
            !this.inEdition && this.customPopupArea(circle, options.areaData);

            this.group.addLayer(circle);

            // Edition mode
            if (inEdition) {

                let pointForMarker = this.convertToLatLng(area.Coords[1]);

                if (area.Coords[0].Lat !== area.Coords[1].Lat) {
                    const properpoint = this.calculateProperPoint(area.Coords[0], area.Coords[1], radius);
                    pointForMarker = this.convertToLatLng(properpoint);
                }

                // Draw slider
                const icon = new DivIcon({ html: '<div class="circle-slider-icon"></div>' });
                const slider = new Marker([pointForMarker.lat, pointForMarker.lng], {
                    icon: icon
                    // icon: new Icon({
                    //     iconUrl: '/assets/img/slider.png',
                    //     iconSize: [32, 26],  // size of the icon
                    //     iconAnchor: [16, 13],  // point of the icon which will correspond to marker's location
                    // })
                });
                this.group.addLayer(slider);

                // Events
                // Slider Events
                const mousemoveSlider = (event: LeafletMouseEvent) => {
                    // Refresh slider
                    const mouse = event.latlng;
                    const originLng = parseFloat(area.Coords[0].Lon);
                    let newLat = mouse.lat;
                    let newLng = mouse.lng;
                    if (this.IS_SLIDER_FIXED) {
                        newLat = slider.getLatLng().lat;
                        newLng = mouse.lng > originLng ? mouse.lng : originLng;
                    }
                    slider.setLatLng([newLat, newLng]);
                    // Refresh circle
                    const _origin = area.Coords[0];
                    const _pointOnCircle = area.Coords[1];
                    _pointOnCircle.Lat = newLat.toString();
                    _pointOnCircle.Lon = newLng.toString();
                    const _radius = this.$map.map!.distance(
                        this.convertToLatLng(_origin),
                        this.convertToLatLng(_pointOnCircle)
                    );
                    circle.setRadius(_radius);
                    // Push changes
                    this.$zone.run(() => {
                        this.drawnAreaSubject.next(area);
                    });
                };

                slider.on('mousedown', (event) => {
                    this.$map.map!.on('mousemove', mousemoveSlider);
                });

                this.$map.map!.on('mouseup', (event) => {
                    this.$map.map!.off('mousemove', mousemoveSlider);
                });

                // Circle Events
                this.bindEventsToShape(circle, area, [slider]);

            } else if (editable) {

                // Draw edit popup if not in edition mode and area is editable
                // this.bindEditPopupTo(circle);

            }

            // Draw label
            // circle.bindTooltip(area.AreaName, { className: 'area-label', permanent: true, direction: 'center' });
            return circle;
        }

        return null as any;
    }


    /**
     * Draws polygon on a map
     * @param area area to draw
     * @param inEdition flag indicating whether given area should be in edition mode
     * @param editable flag indicating whether given area is editable
     */
    private drawPolygon(area: Area, inEdition: boolean, editable: boolean): Polygon<any> {

        // Area Group
        this.group = new FeatureGroup();
        this.$map.groups.areas.addLayer(this.group);

        if (area.Coords.length > 0) {

            // Options
            const weight = this.getWeightInPixels(area);
            const options: any = {
                weight,
                color: area?.Cl || this.AREA_COLOR,
                fillOpacity: this.AREA_COLOR_OPACITY,
                dashArray: this.state === State.PolygonSecondVertex ? '20 20' : '',
                areaData: { ...area },
            };

            // Draw polygon
            const polygon = new Polygon(this.convertToLatLngs(area.Coords), options);
            !this.inEdition && this.customPopupArea(polygon, options.areaData);

            this.group.addLayer(polygon);

            // Edition mode
            if (inEdition) {

                // Markers
                const markers = (this.state === State.PolygonDone) ? this.buildMarkers(polygon, area) : [];

                // Polygon Events
                this.bindEventsToShape(polygon, area, markers);

            } else if (editable) {

                // Draw edit popup if not in edition mode and area is editable
                // this.bindEditPopupTo(polygon);

            }

            return polygon;
            // Draw label
            // polygon.bindTooltip(area.AreaName, { className: 'area-label', permanent: true, direction: 'center' });
        }

        return null as any;
    }


    /**
     * Draws line on a map
     * @param area area to draw
     * @param inEdition flag indicating whether given area should be in edition mode
     * @param editable flag indicating whether given area is editable
     */
    private drawLine(area: Area, inEdition: boolean, editable: boolean): Polyline<any> {

        // Area Group
        this.group = new FeatureGroup();
        this.$map.groups.areas.addLayer(this.group);

        if (area.Coords.length > 0) {

            // Options
            const weight = this.getWeightInPixels(area);
            const options: any = {
                weight,
                color: area?.Cl || this.AREA_COLOR,
                fillOpacity: this.AREA_COLOR_OPACITY,
                dashArray: this.state === State.LineSecondPoint ? '20 20' : '',
                areaData: { ...area },
            };

            // Draw line
            const line = new Polyline(this.convertToLatLngs(area.Coords), options);
            !this.inEdition && this.customPopupArea(line, options.areaData);
            this.group.addLayer(line);

            // Edition mode
            if (inEdition) {

                // Markers
                const markers = (this.state === State.LineDone) ? this.buildMarkers(line, area) : [];

                // Line Events
                this.bindEventsToShape(line, area, markers);

            } else if (editable) {

                // Draw edit popup if not in edition mode and area is editable
                // this.bindEditPopupTo(line);

            }

            // Draw label
            // line.bindTooltip(area.AreaName, { className: `area-label`, permanent: true, direction: 'center', interactive: true });
            return line;
        }
        return null as any;
    }


    /**
     * Compute line weight in pixels from given area thickness
     * @param area area
     */
    private getWeightInPixels(area: Area): number {

        if (area.Tp === AreaTypeEnum.Circle) {
            return this.CIRCLE_DEFAULT_THICKNESS;
        } else if (area.Tp === AreaTypeEnum.Polygon) {
            return Math.floor(this.POLYGON_DEFAULT_THICKNESS);
        } else if (area.Tp === AreaTypeEnum.Line) {
            {
                // const metresPerPixel = 40075016.686 *
                // Math.abs(Math.cos(this.$map.map.getCenter().lat * 180 / Math.PI)) / Math.pow(2, this.$map.map.getZoom() + 8);
                const weight = area.Th / 5.0;  // thickness = 100-105 m -> weight = 20 px
                return Math.floor(weight);
            }
        }
        throw new Error("Unsupported AreaType: " + area.Tp);
    }


    /**
     * Binds edit popup to marker
     * @param layer leaflet layer
     */
    private bindEditPopupTo(layer: Layer) {


        // Draw popup
        const _Popup = Popup.extend({ options: { areaId: null } });
        const popup =  new _Popup();
        popup.className = 'area-edit-popup';
        popup.closeButton = false;
        popup.keepInView = false;
        popup.autoPan = false;
        popup.areaId = this.area!.Id;

        const html = this.getAreaEditPopupHtml();
        popup.setContent(html);
        layer.bindPopup(popup);

        const handler = () => {
            // Event is fired outside angular context
            this.$zone.run(() => {
                this.selectedAreaSubject.next(this.area!);
            });
        };

        layer.on('click', (evt) => {
            // const _latLng = layer.getTooltip().getLatLng();
            // popup.setLatLng(_latLng);
        });


        // Popup Events
        layer.on('popupopen', (event) => {
            DomEvent.addListener(popup.getElement(), 'click', handler);
            const _latLng = layer.getTooltip()!.getLatLng();
            popup.setLatLng(_latLng);
        });

        layer.on('popupclose', (event) => {
            DomEvent.removeListener(popup.getElement(), 'click', handler);
        });
    }


    /**
     * Returns area edit popup HTML
     */
    private getAreaEditPopupHtml(): string {

        const html = `
            <div class="area-edit-popup__content">
                <span class="area-edit-popup__label">Edytuj</span>
            </div>
        `;

        return html;
    }


    /**
     * Converts given point to Leaflet's LatLng object
     * @param point
     */
    private convertToLatLng(_point: Point): LatLng {

        return new LatLng(parseFloat(_point.Lat), parseFloat(_point.Lon));
    }


    /**
     * Converts array of points to array of leaflet's latlngs
     * @param points points
     */
    private convertToLatLngs(points: Point[]): LatLng[] {

        const latLngs: LatLng[] = [];

        for (const _point of points) {
            const _latLng = this.convertToLatLng(_point);
            latLngs.push(_latLng);
        }

        return latLngs;
    }


    /**
     *
     * @param shape
     * @param area
     * @param markers
     */
    private bindEventsToShape(shape: any, area: Area, markers: Marker[] | Circle[]): void {

        const mousemove = (event: LeafletMouseEvent) => {
            this.$zone.run(() => {

                // Refresh shape
                const mouse = event.latlng;
                this.moveShape(shape, area, markers, mouse, this.mousePrev!);
                this.mousePrev = mouse;
                // Push changes
                this.drawnAreaSubject.next(area);
            });
        };


        shape.on('mousedown', (event: LeafletMouseEvent) => {
            this.mousePrev = event.latlng;
            this.$map.map!.on('mousemove', mousemove);
        });

        this.$map.map!.on('mouseup', (event) => {
            this.mousePrev = undefined;
            this.$map.map!.off('mousemove', mousemove);
        });

        // When poligon or line is done draw point creator marker
        if (this.state === State.PolygonDone || this.state === State.LineDone) {

            this.$map.map!.on('mousemove', this.creatorMovementHandler);

            shape.on('mouseout', () => {
                this.$map.map!.on('mousemove', this.creatorMovementHandler);
            });

            shape.on('mouseover', () => {
                this.$map.map!.off('mousemove', this.creatorMovementHandler);
                if (this.creator && this.group) {
                    this.group.removeLayer(this.creator);
                    this.creator = undefined;
                }
            });
        }
    }


    /**
     *
     * @param shape
     * @param area
     * @param markers
     * @param mouse
     * @param mousePrev
     */
    private moveShape(shape: any, area: Area, markers: Marker[] | Circle[], mouse: LatLng, mousePrev: LatLng): void {

        // Compute difference vector
        const latDiff = mouse.lat - mousePrev.lat;
        const lngDiff = mouse.lng - mousePrev.lng;

        // Refresh area
        for (const _point of area.Coords) {
            _point.Lat = (parseFloat(_point.Lat) + latDiff).toString();
            _point.Lon = (parseFloat(_point.Lon) + lngDiff).toString();
        }

        // Refresh markers
        for (const marker of markers) {
            const latlng = marker.getLatLng();
            latlng.lat += latDiff;
            latlng.lng += lngDiff;
            marker.setLatLng(latlng);
        }

        // Refresh shape
        if (shape.setLatLngs) {
            shape.setLatLngs(this.convertToLatLngs(area.Coords));
        } else if (shape.setLatLng) {
            shape.setLatLng(this.convertToLatLng(area.Coords[0]));
        }

        // Refresh tooltip
        const tooltip = shape.getTooltip();
        shape.unbindTooltip();
        shape.bindTooltip(tooltip);
    }


    /**
     *
     * @param shape
     * @param area
     */
    private buildMarkers(shape: any, area: Area): Marker[] {

        const markers: Marker[] = [];

        for (const _point of area.Coords) {

            // const marker = new Circle(this.convertToLatLng(_point), 0, {
            //     weight: 10// * Math.log(this.getWeightInPixels(area))  // Make icon as thick circle
            // });

            const icon = new DivIcon({ html: '<div class="area-drawing-point"><div class="area-drawing-point-content"></div></div>' });
            const marker = new Marker(this.convertToLatLng(_point), { icon: icon });

            this.group!.addLayer(marker);
            markers.push(marker);

            // Bind delete point popup for polygon or line
            if (area.Tp === AreaTypeEnum.Polygon || area.Tp === AreaTypeEnum.Line) {
                this.bindDeletePointPopupTo(marker, area);
            }

            // Point marker Events
            this.bindEventsToMarker(shape, area, marker, _point);
        }

        return markers;
    }


    /**
     *
     * @param shape
     * @param area
     * @param marker
     * @param point
     */
    private bindEventsToMarker(shape: any, area: Area, marker: Marker, _point: Point): void {

        const mousemove = (event: any) => {
            this.$zone.run(() => {
                // Refresh marker
                this.moveMarker(shape, event, area, marker, _point);
                // Push changes
                this.drawnAreaSubject.next(area);
                // marker.setStyle({ color: '#aac8ff' });
            });
        };

        marker.on('mousedown', (event) => {
            this.$map.map!.on('mousemove', mousemove);
        });

        this.$map.map!.on('mouseup', (event) => {
            this.$map.map!.off('mousemove', mousemove);
            // marker.setStyle({ color: this.AREA_COLOR });
        });

        marker.on('mouseover', (event) => {
            // marker.setStyle({ color: '#aac8ff' });
        });

        marker.on('mouseout', (event) => {
            // marker.setStyle({ color: this.AREA_COLOR });
        });


    }


    /**
     * Binds delete point popup to marker
     * @param marker marker
     * @param area area
     */
    private bindDeletePointPopupTo(marker: Marker, area: Area): void {

        // Draw popup
        const popup = new Popup({ 'className': 'delete-point-popup', 'closeButton': false, 'keepInView': false, autoPan: false });
        popup.setContent(this.getDeletePointPopupHtml());
        marker.bindPopup(popup);

        // Popup Events
        let lastMouseDown: number;  // time in millis
        marker.on('mousedown', (event) => {
            lastMouseDown = new Date().getTime();
        });


        const handler = () => {
            this.$zone.run(() => {

                if ((area.Tp === AreaTypeEnum.Polygon && area.Coords.length > 3) ||
                    (area.Tp === AreaTypeEnum.Line && area.Coords.length > 2)) {
                    // Remove vertex from area
                    this.removeVertexFromArea(marker.getLatLng(), area);
                    // Push changes
                    this.drawnAreaSubject.next(area);
                    // Redraw area
                    this.remove();
                    this.setArea(area, this.inEdition);
                    this.draw();
                }
            });
        };

        marker.on('popupopen', (event) => {
            const now = new Date().getTime();
            if (now - lastMouseDown > 300) {
                marker.closePopup();
                return;
            }
            DomEvent.addListener(popup.getElement()!, 'click', handler);
        });
    }


    /**
     * Returns delete point popup HTML
     */
    private getDeletePointPopupHtml(): string {

        const html = `
            <div class="delete-point-popup__content">
                <span class="delete-point-popup__icon"></span>
            </div>
        `;

        return html;
    }


    /**
     * Moves marker to given point
     * @param shape shape
     * @param event leaflet mouse event
     * @param area area
     * @param marker marker to move
     * @param point point where to move marker
     */
    private moveMarker(shape: any, event: LeafletMouseEvent, area: Area, marker: Marker, _point: Point): void {

        // Refresh point
        const latlng = (event as LeafletMouseEvent).latlng;
        _point.Lat = latlng.lat.toString();
        _point.Lon = latlng.lng.toString();

        // Refresh marker
        marker.setLatLng(this.convertToLatLng(_point));

        // Refresh shape
        shape.setLatLngs(this.convertToLatLngs(area.Coords));

        // Refresh tooltip
        const tooltip = shape.getTooltip();
        shape.unbindTooltip();
        shape.bindTooltip(tooltip);
    }


    /**
     * Creates and returns point creator marker
     * @param area area
     * @param point point
     */
    private getCreatorMarker(area: Area, _point: LatLng): CreatorMarker | undefined {

        const zoom = this.$map.map!.getZoom();
        const pointPro = this.$map.map!.project(_point, zoom);

        const segment = this.getClosestAreaSegment(area, _point);
        const segmentPro = segment.map(p => this.$map.map!.project(p, zoom));

        const orthoPro = LineUtil.closestPointOnSegment(pointPro, segmentPro[0], segmentPro[1]);
        const ortho = this.$map.map!.unproject(orthoPro, zoom);

        let marker: CreatorMarker | undefined = undefined;
        if (!this.isNearAreaVertex(ortho, area)) { // && !this.arePointsNear(pointPro, orthoPro)) {
            let a = this.getWeightInPixels(area);
            if (a <= 3) {
                a = 3;
            }
            marker = new CreatorMarker(ortho, 0, {
                weight: 10 * Math.log(a),  // Make icon as thick circle
                color: '#aaa'
            });
            marker.segment = segment;
        }

        return marker;
    }


    /**
     * Returns closest segments to given point
     * @param area area
     * @param point point
     */
    private getClosestAreaSegment(area: Area, _point: LatLng): LatLng[] {

        const zoom = this.$map.map!.getZoom();
        const sortedPoints: Point[] = area.Coords.slice();
        const segments: LatLng[][] = [];
        const init = area.Tp === AreaTypeEnum.Polygon ? 0 : 1;

        for (let i = init; i < area.Coords.length; ++i) {

            const left = (i === 0) ? area.Coords[area.Coords.length - 1] : area.Coords[i - 1];
            const right = area.Coords[i];

            segments.push(this.convertToLatLngs([left, right]));
        }

        const pointPro = this.$map.map!.project(_point, zoom);
        const segmentsPro = segments.map(s => s.map(p => this.$map.map!.project(p, zoom)));

        segmentsPro.sort((a, b) => {
            const closestAPro = LineUtil.closestPointOnSegment(pointPro, a[0], a[1]);
            const closestBPro = LineUtil.closestPointOnSegment(pointPro, b[0], b[1]);
            const closestA = this.$map.map!.unproject(closestAPro, zoom);
            const closestB = this.$map.map!.unproject(closestBPro, zoom);
            const distA = this.$map.map!.distance(closestA, _point);
            const distB = this.$map.map!.distance(closestB, _point);
            return distA - distB;
        });

        const closest = segmentsPro[0].map(p => this.$map.map!.unproject(p, zoom));

        return closest;
    }


    /**
     * Checks whether point is near any vertex of area
     * @param point point
     * @param area area
     */
    private isNearAreaVertex(_point: LatLng, area: Area): boolean {

        const zoom = this.$map.map!.getZoom();
        const pointPro = this.$map.map!.project(_point, zoom);

        const vertices = area.Coords.slice().map(p => this.convertToLatLng(p));
        const verticesPro = vertices.map(p => this.$map.map!.project(p, zoom));

        for (const vertexPro of verticesPro) {
            if (this.arePointsNear(pointPro, vertexPro)) {
                return true;
            }
        }

        return false;
    }


    /**
     * Checks whether two given points are near each other
     * @param p1 first point
     * @param p2 second point
     */
    public arePointsNear(p1: LPoint, p2: LPoint): boolean {

        const limitRadius = 10;  // in pixels
        const xDiff = p1.x - p2.x;
        const yDiff = p1.y - p2.y;
        return Math.pow(xDiff, 2) + Math.pow(yDiff, 2) < Math.pow(limitRadius, 2);
    }


    /**
     * Adds vertex to area
     * @param vertex vertex
     * @param area area
     */
    private addVertexToArea(vertex: LatLng, area: Area): void {

        const found = area.Coords.find(_point => this.creator!.segment![1].equals(this.convertToLatLng(_point)));
        if (found) {
            const fresh = { Lat: vertex.lat.toString(), Lon: vertex.lng.toString() } as Point;
            area.Coords.splice(area.Coords.indexOf(found), 0, fresh);
        }
    }


    /**
     * Remove vertex from area
     * @param vertex vertex
     * @param area area
     */
    private removeVertexFromArea(vertex: LatLng, area: Area): void {

        const found = area.Coords.find(_point => vertex.equals(this.convertToLatLng(_point)));
        if (found) {
            area.Coords.splice(area.Coords.indexOf(found), 1);
        }
    }


    /**
     * Adds creator event hadler callbacks
     */
    public addCreatorEventHandlers(): void {

        if (this.$map.map) {
            this.$map.map.on('mousemove', this.creatorMovementHandler);
            this.$map.map.on('click', this.creatorClickHandler);
        }
    }


    /**
     * Removes creator event hadler callbacks
     */
    public removeCreatorEventHandlers(): void {

        if (this.$map.map) {
            this.$map.map.off('mousemove', this.creatorMovementHandler);
            this.$map.map.off('click', this.creatorClickHandler);
        }
    }


    /**
     * Shows area on the map
     */
    showArea(area: Area): void {
        this.setArea();
        this.remove();
        this.setArea(area);
        this.draw();
        this.fitBoundsToVisibleArea();
    }

    // Add popup to squar, and show/hide when mouse hover square, with area data
    private customPopupArea = (square: any, areaData: Area): void => {
        const customPopupStructureHTML = (): string => {
            const icon = (): string => {
                if (areaData.Tp === AreaTypeEnum.Circle) {
                    return '/assets/img/areas/active@3x.png';
                }
                if (areaData.Tp === AreaTypeEnum.Line) {
                return '/assets/img/areas/active_3@2x.png';
                }
                return '/assets/img/areas/active_4@2x.png';
            };
            return `
                <div class="custom-popup">
                    <div class="custom-popup__details">
                        <div class="custom-popup__details__icon">
                            <img class="custom-popup__image" src="${ icon() }" alt="icon">
                        </div>
                        <div class="custom-popup__details__desc">
                            <span class="custom-popup__details__desc__title">
                                ${ areaData.Nm }
                            </span>
                        </div>
                    </div>

                    <div class="custom-popup__actions">
                        <button id="custom-popup-edit" class="custom-popup__actions__btn">
                            <img id="custom-popup-edit" class="custom-popup__image" src="/assets/img/pen-blue.svg">
                            ${ this.translate.instant('areas.edit') }
                        </button>
                        <button id="custom-popup-delete" class="custom-popup__actions__btn">
                            <img id="custom-popup-delete" class="custom-popup__image" src="/assets/img/trash-blue.svg">
                            ${ this.translate.instant('areas.delete') }
                        </button>
                    </div>
                </div>
            `;
        };
        let onClickEditSubsc: Subscription;
        let onClickDeleteSubsc: Subscription;
        let mouseMoveSubsc: Subscription;
        const onMouseMove = (): void => {
            mouseMoveSubsc = fromEvent<MouseEvent>(document, 'mousemove')
            .pipe(debounceTime(50))
            .subscribe(
                (event) => {
                    const htmlElementUnderMouse = document.elementFromPoint(event.clientX, event.clientY);
                    if (htmlElementUnderMouse) {
                        const ifSquareOrPopup = 
                            (htmlElementUnderMouse.className as any)?.animVal === 'leaflet-interactive' ||
                            htmlElementUnderMouse.className?.includes('custom-popup') ||
                            htmlElementUnderMouse.className?.includes('leaflet-popup-content-wrapper');
        
                        if (!ifSquareOrPopup) {
                            onHidePopup();
                        }
                    }
                }
            );
        }
        const onClickEdit = (): void => {
            onClickEditSubsc = fromEvent<PointerEvent>(document, 'click').subscribe(
                (event) => {
                    const htmlElementUnderMouse = document.elementFromPoint(event.clientX, event.clientY);
                    const isEditBtn = htmlElementUnderMouse?.id === 'custom-popup-edit';
                    if (isEditBtn) {
                        const ifAreaListIsHidden = document.getElementById('container-areas');
                        if (!ifAreaListIsHidden?.classList.contains('is-visible')) {
                            document.getElementById('map-icon-areas')?.click();
                        }
                        this.onClickEditTooltipArea$.next(areaData);
                    }
                }
            );
        };
        const onClickDelete = (): void => {
            onClickDeleteSubsc = fromEvent<PointerEvent>(document, 'click').subscribe(
                (event) => {
                    const htmlElementUnderMouse = document.elementFromPoint(event.clientX, event.clientY);
                    const isEditBtn = htmlElementUnderMouse?.id === 'custom-popup-delete';
                    if (isEditBtn) {
                        this.onClickDeleteTooltipArea$.next(areaData);
                    }
                }
            );
        };
        const onHidePopup = (): void => {
            square.closePopup();
            mouseMoveSubsc.unsubscribe();
            onClickEditSubsc?.unsubscribe();
            onClickDeleteSubsc?.unsubscribe();
        };
        square.bindPopup(customPopupStructureHTML());
        square.on('mouseover', function(this: typeof square) {
            onMouseMove();
            this.openPopup();
            onClickEdit();
            onClickDelete();
            square.getPopup()?.getElement()?.classList.add('popup-square');
        });
    };

}
